import { FocusTable } from "@focus/focusUI";
import type {
  IColumnDefsConfig,
  ITableData,
  RowClassRules,
} from "@focus/focusUI/lib/components/Tables/FocusTable/Entities";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { handleTableFilterChange } from "../../helpers/utils";
import { useHashParams } from "../../hooks/useHashParams";
import type { ServerColumnConfig } from "../../services/helpers/headers";
import type {
  ColumnFiltersState,
  SortingState,
} from "../../services/helpers/pager";
import {
  getInputOrbitFilesPageResponse,
  type InputOrbitRow,
} from "../../services/inputs";
import { FiltersTag } from "../table/FiltersTag";
import { type TableActionType } from "../table/TableActionColumn";
import { TableSideBar } from "../table/TableSideBar";
import { useFocusocTable, type OnChangeFn } from "../table/useFocusocTable";

interface InputOrbitTableProps {
  actions: TableActionType[];
  base: string;
  columnDefinitions: IColumnDefsConfig[];
  controlledColumnFilter: ColumnFiltersState;
  controlledColumnFilterChange: OnChangeFn<ColumnFiltersState>;
  defaultVisibleValues: string[];
  downloadData: (param: { id: string }) => void;
  initialSorting?: SortingState;
  refreshKey?: unknown;
  rowClassRules?: RowClassRules;
  saveColumnLayout?: (serverConfig: ServerColumnConfig) => void;
  serverDisplayConfig: ServerColumnConfig;
  showOperatorColumn?: boolean;
  showSidebar?: boolean;
  source?: string;
}

export const InputOrbitTable = ({
  actions,
  base,
  columnDefinitions,
  controlledColumnFilter,
  controlledColumnFilterChange,
  defaultVisibleValues,
  downloadData,
  initialSorting = [],
  refreshKey,
  rowClassRules,
  saveColumnLayout,
  serverDisplayConfig,
  showOperatorColumn = true,
  showSidebar = true,
  source,
}: InputOrbitTableProps) => {
  const { getParam, setParam } = useHashParams();
  const [data, setData] = useState<ITableData[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [globalFilter, setGlobalFilter] = useState("");
  const selectedFilterFnRef = useRef<Record<string, string>>({});

  const initialPageIndex = useMemo(() => {
    const pageFromUrl = Number(getParam("page"));
    return Number.isFinite(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 0;
  }, []);

  const [pagination, setPagination] = useState({
    pageIndex: initialPageIndex,
    pageSize: 30,
  });

  const {
    columnDefs,
    columnVisibility,
    columnOrder,
    toggleVisibility,
    handleColumnOrderChange,
    handleColumnResize,
  } = useFocusocTable({
    serverDisplayConfig,
    defaultVisibleValues,
    columnDefinitions,
    actions,
    base,
    source,
    downloadData,
    saveColumnLayout,
    showOperatorColumn,
  });

  const handleChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      const filtered = handleTableFilterChange(
        updater,
        controlledColumnFilter,
        selectedFilterFnRef.current,
      );
      controlledColumnFilterChange(filtered);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    [controlledColumnFilter, controlledColumnFilterChange],
  );

  useEffect(() => {
    setParam("page", pagination.pageIndex.toString());
  }, [pagination.pageIndex]);

  useEffect(() => {
    const pageFromUrl = Number(getParam("page"));

    if (Number.isFinite(pageFromUrl) && pageFromUrl !== pagination.pageIndex) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: pageFromUrl,
      }));
    }
  }, [getParam("page")]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        const page = await getInputOrbitFilesPageResponse({
          page: pagination.pageIndex,
          size: pagination.pageSize,
          filter: controlledColumnFilter,
          sort: sorting
        });

        if (cancelled) return;

        setData(page.content);
        setTotalRows(page.totalElements);
      } catch (error) {
        console.error(error);
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [pagination, sorting, controlledColumnFilter, refreshKey]);

  const paginationOptions = useMemo(
    () => ({
      pageCount: Math.max(Math.ceil(totalRows / pagination.pageSize), 1),
      manualPagination: {
        pagination,
        setPagination,
      },
    }),
    [totalRows, pagination],
  );

  const hasFilters = controlledColumnFilter.length > 0;

  return (
    <div className="flex h-full min-h-0 min-w-0 overflow-hidden">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {hasFilters && (
          <div className="flex flex-shrink-0 justify-end px-3 pt-2">
            <FiltersTag
              columnFilters={controlledColumnFilter}
              columnFiltersChange={controlledColumnFilterChange}
              columnDefinitions={columnDefinitions}
            />
          </div>
        )}
        <FocusTable
          className="min-w-0 flex-1"
          data={data}
          setData={setData}
          columnDefs={columnDefs}
          getRowId={(row: InputOrbitRow) => row.id}
          paginated
          manualFiltering
          manualSorting
          controlledSorting={sorting}
          controlledSortingChange={(updater) => {
            setSorting(updater);
            setPagination((current) => ({ ...current, pageIndex: 0 }));
          }}
          controlledColumnFilters={controlledColumnFilter}
          controlledColumnFilterChange={handleChange}
          paginationOptions={paginationOptions}
          height="auto"
          controlledGlobalFilter={globalFilter}
          controlledGlobalFilterChange={setGlobalFilter}
          controlledColumnVisibility={columnVisibility}
          onColumnResize={handleColumnResize}
          rowClassRules={rowClassRules}
          controlledColumnOrder={columnOrder}
          controlledColumnOrderChange={handleColumnOrderChange}
          selectedFilterFnRef={selectedFilterFnRef}
        />
      </div>
      {showSidebar && (
        <TableSideBar
          columnDefinitions={columnDefs}
          columnVisibility={columnVisibility}
          toggleVisibility={toggleVisibility}
          columnOrder={columnOrder}
          handleColumnOrderChange={handleColumnOrderChange}
        />
      )}
    </div>
  );
};
